import type { PromoCode } from '../promo/codes'
import {
  type BillingPeriod,
  childPrice,
  type Plan,
  type PlanPrice,
  type Price,
  type PurchasablePlan,
  plansFor,
} from './catalogue'

/**
 * Every number the checkout displays, on the page and on the cart's lines, is
 * derived here from the catalogue's integer-cent charge amounts. All
 * arithmetic stays in integer cents; formatting happens only at the very edge,
 * in the formatters at the end.
 *
 * No amount is rounded: every charge is a catalogue amount as is, and
 * `catalogue.test.ts` requires every annual price, a sibling's included, to be
 * divisible by 12, so the per-kit rate that is a twelfth of one is whole
 * cents. Percentages are floored, so a claim never overstates a saving.
 */

/** How many months a billing period's charge covers. */
const MONTHS: Record<BillingPeriod, number> = { monthly: 1, annual: 12 }

/** The per-kit / per-month rate implied by the annual charge. */
export function annualEffectiveCents(plan: Plan): number {
  return plan.annual.priceCents / 12
}

/** The headline rate shown on a plan card for the given billing period. */
export function rateCents(plan: Plan, period: BillingPeriod): number {
  return period === 'annual' ? annualEffectiveCents(plan) : plan.monthly.priceCents
}

/** The whole percent `paidCents` saves on `fullCents` — floored, so we never overstate it. */
function percentOff(fullCents: number, paidCents: number): number {
  // Integer numerator and denominator, deliberately: the floating-point form
  // 100 * (1 - paid/full) turns a true 20% into 19.999…, which would floor to
  // 19 and understate the claim.
  return Math.floor((100 * (fullCents - paidCents)) / fullCents)
}

/**
 * What each billing period costs over its first year, as charged: the first
 * monthly charge and eleven more at the price, against one annual charge.
 * Only a monthly plan's first year costs less than its later ones, so this is
 * the year paying annually saves least, and a claim made from it holds every
 * year.
 */
function firstYearCents(plan: Plan): Record<BillingPeriod, number> {
  return {
    monthly: firstChargeCents(plan.monthly) + (MONTHS.annual - 1) * plan.monthly.priceCents,
    annual: firstChargeCents(plan.annual),
  }
}

/** "Save $104.40" on an annual card: a year paying annually against a year paying monthly. */
export function annualSavingsCents(plan: Plan): number {
  const year = firstYearCents(plan)
  return year.monthly - year.annual
}

/** "(25%)" beside it: the same saving as a whole percent of the year paying monthly. */
export function annualSavingsPercent(plan: Plan): number {
  const year = firstYearCents(plan)
  return percentOff(year.monthly, year.annual)
}

/** "Save up to 25%" on the billing toggle: the largest saving any plan offers the visitor. */
export function bestSavingsPercent(promo: PromoCode | null): number {
  return Math.max(...plansFor(promo).map(annualSavingsPercent))
}

/** "Save 10% on their kit": a sibling's price against the first child's, per billing cycle. */
export function siblingSavingsPercent(plan: PurchasablePlan, period: BillingPeriod): number {
  return percentOff(plan[period].priceCents, plan.sibling[period].priceCents)
}

/** The displayed per-kit rate for the child at `index` — a twelfth of the annual price. */
export function perChildRateCents(
  plan: PurchasablePlan,
  period: BillingPeriod,
  index: number,
): number {
  return childPrice(plan, period, index).priceCents / MONTHS[period]
}

/** What a first charge costs: the price, less any first-kit discount. */
export function firstChargeCents(price: Price): number {
  return price.priceCents - (price.firstKitDiscountCents ?? 0)
}

/**
 * What every charge after the first costs. An annual charge's first kit is
 * discounted every year, but a monthly plan's first kit only once.
 */
function recurringCents(price: Price, period: BillingPeriod): number {
  return period === 'annual' ? firstChargeCents(price) : price.priceCents
}

/** What a discount on a cart line is for. */
export type DiscountKind = 'sibling' | 'annual' | 'first-kit'

/** One discount on a cart line: its whole percent, floored, and what it takes off. */
export interface LineDiscount {
  kind: DiscountKind
  percent: number
  cents: number
}

/**
 * The discounts on the line of the child at `index`, in the order they apply:
 * the sibling discount, the annual one, then any on the first kit. Together
 * they make up exactly what the line's first charge is less than the plan's
 * monthly price over the billing period. The sibling and annual discounts
 * step down to the line's price, each taken from the price the one before
 * left; the first kit's is one kit's, so its percent is of one kit's price.
 */
export function lineDiscounts(
  plan: PurchasablePlan,
  period: BillingPeriod,
  index: number,
): LineDiscount[] {
  const months = MONTHS[period]
  const price = childPrice(plan, period, index)

  // Each discount, and the price it leaves.
  const steps: [DiscountKind, number][] = []
  if (index > 0) steps.push(['sibling', months * plan.sibling.monthly.priceCents])
  if (period === 'annual') steps.push(['annual', price.priceCents])

  let from = months * plan.monthly.priceCents
  const discounts: LineDiscount[] = steps.map(([kind, to]) => {
    const discount = { kind, percent: percentOff(from, to), cents: from - to }
    from = to
    return discount
  })

  const firstKit = price.firstKitDiscountCents
  if (firstKit !== undefined) {
    discounts.push({ kind: 'first-kit', percent: firstKitPercent(price, period), cents: firstKit })
  }
  return discounts
}

/** "40% off first month": the first-kit discount as a whole percent of one kit's price. */
export function firstKitPercent(price: Price, period: BillingPeriod): number {
  const kit = price.priceCents / MONTHS[period]
  return percentOff(kit, kit - (price.firstKitDiscountCents ?? 0))
}

/** One amount per child, by the rule `childPrice` sets, added up across the order. */
function sumOverChildren(
  plan: PurchasablePlan,
  period: BillingPeriod,
  childCount: number,
  amount: (price: PlanPrice) => number,
): number {
  let total = 0
  for (let index = 0; index < childCount; index++) {
    total += amount(childPrice(plan, period, index))
  }
  return total
}

/** The whole order, per billing cycle after the first: what every renewal charges. */
export function orderTotalCents(
  plan: PurchasablePlan,
  period: BillingPeriod,
  childCount: number,
): number {
  return sumOverChildren(plan, period, childCount, (price) => recurringCents(price, period))
}

/** What the order's first charge costs: every child's first charge, added up. */
export function dueTodayCents(
  plan: PurchasablePlan,
  period: BillingPeriod,
  childCount: number,
): number {
  return sumOverChildren(plan, period, childCount, firstChargeCents)
}

/**
 * What the order saves per billing cycle against every child paying the plan's
 * monthly price: the sibling discount, and on an annual plan the annual one
 * and the first kit's too, which come off every year.
 */
export function orderSavingsCents(
  plan: PurchasablePlan,
  period: BillingPeriod,
  childCount: number,
): number {
  const atMonthlyPrice = childCount * MONTHS[period] * plan.monthly.priceCents
  return atMonthlyPrice - orderTotalCents(plan, period, childCount)
}

const preciseDollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

/** $2995 -> "$29.95"; whole dollars drop the cents ($178800 -> "$1,788"). */
export function formatCents(cents: number): string {
  const format = cents % 100 === 0 ? wholeDollars : preciseDollars
  return format.format(cents / 100)
}

/** $4200 -> "$42.00": always to the cent, the way Shopify writes amounts on a line. */
export function formatPreciseCents(cents: number): string {
  return preciseDollars.format(cents / 100)
}
